import {useRef, useState, type PointerEvent} from 'react';

const DRAG_THRESHOLD_PX = 6;

type Point = {x: number; y: number};
type Bounds = {minX: number; maxX: number; minY: number; maxY: number};
type DragStart = Point & {offset: Point; bounds: Bounds};

const ORIGIN: Point = {x: 0, y: 0};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

// The offset is meant for the CSS translate property, not transform, so it does not fight a hover/active scale.
export function useDragOffset() {
    const [offset, setOffset] = useState<Point>(ORIGIN);
    const startRef = useRef<DragStart | null>(null);
    const didDragRef = useRef(false);

    const onPointerDown = (e: PointerEvent<HTMLElement>) => {
        if(e.button !== 0) {
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        startRef.current = {
            x: e.clientX,
            y: e.clientY,
            offset,
            bounds: {
                minX: offset.x - rect.left,
                maxX: offset.x + window.innerWidth - rect.right,
                minY: offset.y - rect.top,
                maxY: offset.y + window.innerHeight - rect.bottom,
            },
        };
        didDragRef.current = false;
        e.currentTarget.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: PointerEvent<HTMLElement>) => {
        const start = startRef.current;
        if(!start) {
            return;
        }
        const deltaX = e.clientX - start.x;
        const deltaY = e.clientY - start.y;
        if(!didDragRef.current && Math.abs(deltaX) < DRAG_THRESHOLD_PX && Math.abs(deltaY) < DRAG_THRESHOLD_PX) {
            return;
        }
        didDragRef.current = true;
        setOffset({
            x: clamp(start.offset.x + deltaX, start.bounds.minX, start.bounds.maxX),
            y: clamp(start.offset.y + deltaY, start.bounds.minY, start.bounds.maxY),
        });
    };

    const onPointerUp = () => {
        startRef.current = null;
    };

    const onPointerCancel = () => {
        onPointerUp();
        didDragRef.current = false;
    };

    const shouldSkipClick = () => {
        const didDrag = didDragRef.current;
        didDragRef.current = false;
        return didDrag;
    };

    return {
        offset,
        dragHandlers: {onPointerDown, onPointerMove, onPointerUp, onPointerCancel},
        shouldSkipClick,
    };
}
